import { BaseApiClient } from './base-client'
import {
  API_ROUTES,
  interpolateRoute,
  type ApiResponse,
  type PartnerResponseDto,
  type CreatePartnerDto,
  type PartnerListResponseDto,
  type GeneratePartnerCredentialsResponseDto,
  type PartnerStatus,
} from '@super/shared-types'

export class AdminPartnersClient extends BaseApiClient {
  async createPartner(request: CreatePartnerDto): Promise<PartnerResponseDto> {
    const response = await this.request<ApiResponse<PartnerResponseDto>>(
      API_ROUTES.ADMIN.PARTNERS.CREATE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    )
    return response.data!
  }

  async listPartners(): Promise<PartnerResponseDto[]> {
    const response = await this.request<ApiResponse<PartnerListResponseDto>>(
      API_ROUTES.ADMIN.PARTNERS.LIST,
      {
        method: 'GET',
      },
    )
    return response.data?.partners ?? []
  }

  async getPartner(partnerId: string): Promise<PartnerResponseDto> {
    const url = API_ROUTES.ADMIN.PARTNERS.GET.replace(':id', partnerId)
    const response = await this.request<ApiResponse<PartnerResponseDto>>(url, {
      method: 'GET',
    })
    return response.data!
  }

  async updatePartnerStatus(
    partnerId: string,
    status: PartnerStatus,
  ): Promise<PartnerResponseDto> {
    const url = API_ROUTES.ADMIN.PARTNERS.UPDATE_STATUS.replace(
      ':id',
      partnerId,
    )
    const response = await this.request<ApiResponse<PartnerResponseDto>>(url, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
    return response.data!
  }

  async updatePartnerSipTrunkId(
    partnerId: string,
    sipTrunkId: string | null,
  ): Promise<PartnerResponseDto> {
    const url = interpolateRoute(API_ROUTES.ADMIN.PARTNERS.UPDATE_SIP_TRUNK, {
      id: partnerId,
    })
    const response = await this.request<ApiResponse<PartnerResponseDto>>(url, {
      method: 'PATCH',
      body: JSON.stringify({ sipTrunkId }),
    })
    return response.data!
  }

  async generateCredentialsForPartner(
    partnerId: string,
  ): Promise<GeneratePartnerCredentialsResponseDto> {
    const url = API_ROUTES.ADMIN.PARTNERS.GENERATE_CREDENTIALS.replace(
      ':id',
      partnerId,
    )
    const response = await this.request<
      ApiResponse<GeneratePartnerCredentialsResponseDto>
    >(url, {
      method: 'POST',
    })
    return response.data!
  }

  async getPartnerAudit(
    partnerId: string,
    params: {
      action?: string
      resourceType?: string
      limit?: number
      offset?: number
    } = {},
  ): Promise<PartnerAuditLogPage> {
    const query = new URLSearchParams()
    if (params.action) query.set('action', params.action)
    if (params.resourceType) query.set('resourceType', params.resourceType)
    if (params.limit != null) query.set('limit', String(params.limit))
    if (params.offset != null) query.set('offset', String(params.offset))
    const base = interpolateRoute(API_ROUTES.ADMIN.PARTNERS.GET_PARTNER_AUDIT, {
      id: partnerId,
    })
    const url = query.toString() ? `${base}?${query.toString()}` : base
    const response = await this.request<ApiResponse<PartnerAuditLogPage>>(url, {
      method: 'GET',
    })
    return response.data!
  }
}

export interface PartnerAuditLogEntry {
  id: string
  partnerId: string
  action: string
  resourceType: string
  resourceId: string | null
  previousState: Record<string, unknown> | null
  newState: Record<string, unknown> | null
  success: boolean
  errorCode: string | null
  errorMessage: string | null
  ipAddress: string | null
  userAgent: string | null
  requestId: string | null
  createdAt: string
}

export interface PartnerAuditLogPage {
  logs: PartnerAuditLogEntry[]
  total: number
  limit: number
  offset: number
}

export const adminPartnersClient = new AdminPartnersClient()
